import { DefinitionActionSubKindEnum } from '@acme/api';
import { Separator, formatDate } from '@acme/foundation';
import { useMemo, type ReactNode } from 'react';
import { ApplicationEpoch } from '../../features/applicationEpoch';

export type ApplicationCardApplication = {
  id?: string;
  code?: string | null;
  epoch?: { id?: number; name?: string | null } | null;
  actions?: Array<{ subKind?: number }> | null;
  createdAt?: string;
  updatedAt?: string;
};

export interface ApplicationCardProps {
  application: ApplicationCardApplication;
  onClick?: () => void;
  amount?: number;
  currency?: string;
  children?: ReactNode;
}

const hintBySubKind: { [key in DefinitionActionSubKindEnum]?: string } = {
  [DefinitionActionSubKindEnum.UploadTransferCommessionRecepotDoc]:
    'Ожидается подтверждение денежного перевода',
};

export const ApplicationCard: React.FC<ApplicationCardProps> = ({
  application,
  onClick,
  amount,
  currency,
  children,
}) => {
  const formatAmount = (value?: number) => {
    if (!value) return '';
    return new Intl.NumberFormat('ru-RU').format(value);
  };

  const subKind = useMemo(() => {
    if (application) {
      const action = application.actions?.find((a) => a.subKind);
      return action?.subKind;
    }
    return null;
  }, [application]);

  return (
    <div
      className="bg-white border border-gray-200 border-b-0 rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] cursor-pointer hover:shadow-lg transition-shadow w-full max-w-[500px]"
      onClick={onClick}
    >
      {/* Верхняя секция */}
      <div className="px-6 py-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-xl font-semibold text-gray-900 leading-[1.5]">
            {application.code || '—'}
          </h3>

          {amount && (
            <div className="flex items-center gap-1.5 text-xl font-semibold leading-[1.5] whitespace-nowrap">
              <span className="text-gray-900">{formatAmount(amount)}</span>
              <span className="text-gray-500">{currency || 'USD'}</span>
            </div>
          )}
        </div>
        {(application.createdAt || application.updatedAt) && (
          <div className="flex flex-wrap gap-x-4 gap-y-0 text-sm text-gray-500">
            {application.createdAt && (
              <span>Создано: {formatDate(application.createdAt)}</span>
            )}
            {application.updatedAt && (
              <span>Обновлено: {formatDate(application.updatedAt)}</span>
            )}
          </div>
        )}
      </div>
      <Separator />
      {children && (
        <div className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}
      {/* Нижняя секция */}
      <div className="px-6 py-4 flex flex-col gap-2">
        <ApplicationEpoch
          epoch={application.epoch}
          hint={
            subKind != null
              ? hintBySubKind?.[subKind as DefinitionActionSubKindEnum]
              : null
          }
        />
      </div>
    </div>
  );
};
